import { createContext } from 'react';
import type { JSX, Dispatch } from 'react';

import { NULL_FUNCTION } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { EMPTY_ID } from '@e-socna/empty-client';

import { initialState } from './types/state.type';
import { useUiReducer } from './hooks/useReducer.hook';
import type { BackgroundOperationType } from './enums/background-operation-type.enum';
import type { BackgroundOperationKey } from './types/background-operation-key.type';
import type { UiContextAction } from './types/actions.type';
import type { UiState } from './types/state.type';

interface UiData {
  readonly ui: UiState;
  readonly dispatchUi: Dispatch<UiContextAction>;
  readonly enableTextEditorFullScreenMode: (isEnabled: boolean) => void;
  readonly enableImageEditorFullScreenMode: (isEnabled: boolean) => void;
  readonly startBackgroundOperation: (
    key: Readonly<BackgroundOperationKey>,
  ) => void;
  readonly endBackgroundOperation: (
    key: Readonly<BackgroundOperationKey>,
  ) => void;
  readonly endBackgroundOperationsByType: (
    operationType: BackgroundOperationType,
  ) => void;
  readonly showDataGrid: () => Id;
  readonly hideDataGrid: (token: Id) => void;
}

const UiContext = createContext<UiData>({
  ui: initialState,
  dispatchUi: NULL_FUNCTION,
  enableTextEditorFullScreenMode: NULL_FUNCTION,
  enableImageEditorFullScreenMode: NULL_FUNCTION,
  startBackgroundOperation: NULL_FUNCTION,
  endBackgroundOperation: NULL_FUNCTION,
  endBackgroundOperationsByType: NULL_FUNCTION,
  showDataGrid: () => EMPTY_ID,
  hideDataGrid: NULL_FUNCTION,
});

interface Props {
  readonly children: JSX.Element;
}

const UiContextProvider = ({ children }: Readonly<Props>): JSX.Element => {
  const uiReducer = useUiReducer();

  return <UiContext.Provider value={uiReducer}>{children}</UiContext.Provider>;
};

export { UiContext, UiContextProvider };
